import { map, finalize, tap, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { DataService } from '../../../core/service/base/data.service';
import { Observable } from 'rxjs';
import { IAppConfig } from './../../../app.config';
import { Contract } from '../../model/contract';
import { LeaseAgreementStatus, SubleaseStatus } from '../../model/const/enums';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Company } from '../../model/company';
import { Property } from '../../model/property';
import { Article } from '../../model/article';
import { LocalDate } from '../../../core/model/local-date';
import { Representative } from '../../model/representative';
import { LeaseRow } from '../../model/lease-row';

export interface IEmailReceiver {
  id: string;
  emailReceivers: string;
}
export interface ILetterReceiver {
  id: string;
  contractNumber: string;
  recipientName: string;
}
export interface ISmsReceiver {
  id: string;
  smsReceiver: string;
}

export abstract class ContractService<TModel extends Contract> extends DataService<TModel> {
  private readonly _defaultFilterFields: string[] = ['company', 'property', 'status', 'tenant', 'rentalId', 'street'];

  constructor(
    httpClient: HttpClient,
    config: IAppConfig,
    messageService: MessageService,
    modelConstructor: { new (): TModel },
    progressBar: ProgressBarService,
    serviceEndPoint: string,
    readonly exportFilename: string = 'Hyreskontrakt.xlsx'
  ) {
    super(httpClient, config, messageService, modelConstructor, progressBar, serviceEndPoint);
    this.leaseAgreementRowEndpoint = `${config.apiEndpointV2}/lease-agreement-row`;
  }

  private readonly leaseAgreementRowEndpoint: string;

  protected createModel(data: any): TModel {
    const model = super.createModel(data);

    if (data.otherLeaseAgreements instanceof Array) {
      model.otherLeaseAgreements = data.otherLeaseAgreements.map(item => Object.assign(new Contract(item.category), item));
    }

    if (data.representatives instanceof Array) {
      model.representatives = data.representatives.map(item => Object.assign(new Representative(), item));
    }

    return model;
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, this.exportFilename);
  }

  exportNoPersonalData(ids: string[]): Observable<File> {
    return super.exportDataItemsExcludePersonalData(ids, this.exportFilename);
  }

  exportFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, this.exportFilename, true);
  }

  exportFilteredNoPersonalData(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFilteredWithUrl(
      null,
      'export-filter-no-personal-data',
      filter,
      includedIds,
      excludedIds,
      affectedItemsCount,
      this.exportFilename,
      true
    );
  }

  getEmailFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<IEmailReceiver[]> {
    this.progressBar.start();
    return this.client
      .post<IEmailReceiver[]>(`${this.endpoint}/email-filter`, {
        filter: this.prepareFilter(filter),
        includedIds: includedIds,
        excludedIds: excludedIds,
        affectedItemsCount: affectedItemsCount,
      })
      .pipe(
        // Without this the failure reaches the caller as an unhandled observable error, and the
        // send-message lists show nothing at all: no message, no form, just a stopped progress bar.
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getLetterFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<ILetterReceiver[]> {
    this.progressBar.start();
    return this.client
      .post<ILetterReceiver[]>(`${this.endpoint}/letter-filter`, {
        filter: this.prepareFilter(filter),
        includedIds: includedIds,
        excludedIds: excludedIds,
        affectedItemsCount: affectedItemsCount,
      })
      .pipe(
        // Without this the failure reaches the caller as an unhandled observable error, and the
        // send-message lists show nothing at all: no message, no form, just a stopped progress bar.
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getSmsFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<ISmsReceiver[]> {
    this.progressBar.start();
    return this.client
      .post<ISmsReceiver[]>(`${this.endpoint}/sms-filter`, {
        filter: this.prepareFilter(filter),
        includedIds: includedIds,
        excludedIds: excludedIds,
        affectedItemsCount: affectedItemsCount,
      })
      .pipe(
        // Without this the failure reaches the caller as an unhandled observable error, and the
        // send-message lists show nothing at all: no message, no form, just a stopped progress bar.
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  approve(id: string): Observable<LeaseAgreementStatus | {}> {
    this.progressBar.start();
    return this.client.put<LeaseAgreementStatus>(`${this.endpoint}/approve`, JSON.stringify(id)).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_APPROVED')),
      finalize(() => this.progressBar.stop())
    );
  }

  getFilterDefinition(): Observable<{
    company: string;
    companies: Company[];
    property: string;
    properties: Property[];
    selectedFields: string[];
    status: number;
    street: string;
    city: string;
    tenant: string;
    tenantContactIdentification: string;
    leaseAgreementNumber: string;
    note: string;
    popularName: string;
    rentalId: string;
    leasePeriodType: number;
    article: string;
    articles: Article[];
    rentFromYear: number;
    rentFromMonth: number;
    terminationMonitoringStatus: number;
    invoiceDeliveryMethod: number;
    leaseAgreementRowIndexClause: number;
    defaultFields: string[];
    subleaseStatus: SubleaseStatus;
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    building: string;
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : '',
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          property: res.property ? res.property : '',
          properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          status: res.status ? res.status : 0,
          street: res.street ? res.street : '',
          city: res.city ? res.city : '',
          tenant: res.tenant ? res.tenant : '',
          tenantContactIdentification: res.tenantContactIdentification ? res.tenantContactIdentification : '',
          leaseAgreementNumber: res.leaseAgreementNumber ? res.leaseAgreementNumber : '',
          note: res.note ? res.note : '',
          popularName: res.popularName ? res.popularName : '',
          rentalId: res.rentalId ? res.rentalId : '',
          leasePeriodType: res.leasePeriodType ? res.leasePeriodType : null,
          article: res.article ? res.article : '',
          articles: Array.isArray(res.articles) ? res.articles.map(x => Object.assign(new Article(), x)) : [],
          rentFromYear: LocalDate.today().year,
          rentFromMonth: LocalDate.today().month,
          terminationMonitoringStatus: res.terminationMonitoringStatus ? res.terminationMonitoringStatus : 0,
          invoiceDeliveryMethod: res.invoiceDeliveryMethod || '',
          leaseAgreementRowIndexClause: res.leaseAgreementRowIndexClause ? res.leaseAgreementRowIndexClause : null,
          defaultFields: this._defaultFilterFields,
          subleaseStatus: res.subleaseStatus ? res.subleaseStatus : 0,
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          building: res.building ? res.building : '',
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getNoticePeriods(id: string): Observable<any> {
    return this.client.get(`${this.endpoint}/notice-period/${id}`);
  }

  print(id: any): Observable<Blob> {
    return this.getPdf(`print/${id}`);
  }

  terminate(context: any): Observable<LeaseAgreementStatus | any> {
    return this.client.put<LeaseAgreementStatus>(`${this.endpoint}/terminate`, JSON.stringify(context)).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw new Error();
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_TERMINATED'))
    );
  }

  withdrawApproval(id: string): Observable<LeaseAgreementStatus> {
    this.progressBar.start();
    return this.client.put<LeaseAgreementStatus>(`${this.endpoint}/withdraw-approval`, JSON.stringify(id)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_WITHDRAW_APPROVAL')),
      finalize(() => this.progressBar.stop())
    );
  }

  withdrawTermination(id: string): Observable<any> {
    return this.client.put<LeaseAgreementStatus>(`${this.endpoint}/withdraw-termination`, JSON.stringify(id)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_WITHDRAW_TERMINATION')),
      finalize(() => this.progressBar.stop())
    );
  }

  applyLeaseAgreementRow(id: string, rowModel: LeaseRow): Observable<LeaseRow[]> {
    if (rowModel.voided) {
      rowModel.toDate = rowModel.fromDate;
      rowModel.toDate = LocalDate.fromDate(rowModel.toDate.addDays(-1));
    }
    const queryModel = {
      entityId: id,
      rowModel: rowModel,
    };

    this.progressBar.start();
    return this.client.put<LeaseRow[]>(`${this.leaseAgreementRowEndpoint}/apply-row`, JSON.stringify(queryModel)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_APPLY_ROW')),
      finalize(() => this.progressBar.stop())
    );
  }

  editLeaseAgreementRow(id: string, rowModel: LeaseRow): Observable<LeaseRow[]> {
    if (rowModel.voided) {
      rowModel.toDate = rowModel.fromDate;
      rowModel.toDate = LocalDate.fromDate(rowModel.toDate.addDays(-1));
    }
    const queryModel = {
      entityId: id,
      rowModel: rowModel,
    };

    this.progressBar.start();
    return this.client.put<LeaseRow[]>(`${this.leaseAgreementRowEndpoint}/edit-row`, JSON.stringify(queryModel)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_APPLY_ROW')),
      finalize(() => this.progressBar.stop())
    );
  }

  removeLeaseAgreementRow(entityId: string, rowId: string): Observable<LeaseRow[]> {
    const queryModel = {
      entityId: entityId,
      rowId: rowId,
    };

    this.progressBar.start();
    return this.client.put<LeaseRow[]>(`${this.leaseAgreementRowEndpoint}/remove-row`, JSON.stringify(queryModel)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      tap(() => this.notification.showSuccess('SUCCESS.LEASE_AGREEMENT_REMOVE_ROW')),
      finalize(() => this.progressBar.stop())
    );
  }
}
